interface Bound<TScore> {
  lower: number;
  upper: number;
  score: TScore;
}

export type Bounds<TScore> = Bound<TScore>[];

export type VitalParameterScore = 0 | 1 | 2 | 3;
export type VitalParameterType = 'high' | 'low' | 'normal';

export interface VitalParameterResult {
  value: VitalParameterScore;
  type: VitalParameterType;
}

export type VitalParameterBounds = Bounds<VitalParameterResult>;

export type Scale<TScore> = (value: number) => TScore;

/**
 * Function to be used for interpolating values within bounds. May throw if value is not handled by
 * bounds
 */
export type VitalParameterScale = Scale<VitalParameterResult>;

export enum Consciousness {
  Alert = 'alert',
  Confusion = 'confusion',
  Verbal = 'verbal',
  Pain = 'pain',
  Unresponsive = 'unresponsive',
}

export enum OxygenSaturationScale {
  Scale1 = 'scale-1',
  Scale2Air = 'scale-2-air',
  Scale2Oxygen = 'scale-2-oxygen',
}

/**
 * Clinical risk levels as defined by NEWS2. These values are defined by `ClinicalRiskBounds`
 */
export enum NEWS2ClinicalRisk {
  /**
   * A score from 0-4
   *
   * Ward-based response
   */
  Low = 'low',
  /**
   * Any individual score of 3 should trigger this as the minimum value
   *
   * Urgent ward-based response
   */
  LowMedium = 'low-medium',
  /**
   * A score from 5-6
   *
   * Key threshold for urgent response
   */
  Medium = 'medium',
  /**
   * A score of 7+
   *
   * Urgent or emergency response
   */
  High = 'high',
}

export type NEWS2RiskBounds = Bound<NEWS2ClinicalRisk>[];

export type Integer = number;

/**
 * Percent values should be an integer that represents the relevant value as an integer between
 * 0 and 100
 */
export type Percentage = Integer;

/**
 * A float value with a single decimal place
 */
export type SingleDecimalFloat = number;

/**
 * Required data for calculating the NEWS2 Score - the calculation of this score depends on all 6
 * parameters being fully defined
 */
export interface NEWS2VitalParameters {
  /**
   * Determines how oxygen saturation score should be calculated calculated
   */
  oxygenSaturationScale: OxygenSaturationScale;
  oxygenSaturation: Percentage;

  pulseRate: Integer;
  respirationRate: Integer;
  systolicBloodPressure: Integer;
  temperature: SingleDecimalFloat;
  consciousness: Consciousness;
}

export interface QuroScoreParameters {
  /**
   * Determines how oxygen saturation score should be calculated calculated
   */
  oxygenSaturationScale: OxygenSaturationScale;

  pulseRate: Integer;
  respirationRate: Integer;
  temperature: SingleDecimalFloat;
  consciousness: Consciousness;

  oxygenSaturation?: Percentage;
  systolicBloodPressure?: Integer;
}

export interface NEWS2VitalParameterScores {
  oxygenSaturation: VitalParameterResult;
  pulseRate: VitalParameterResult;
  respirationRate: VitalParameterResult;
  systolicBloodPressure: VitalParameterResult;
  temperature: VitalParameterResult;
  consciousness: VitalParameterScore;
  /**
   * A score appended depending on the oxygen scale a patient is on. Represents the `Air or Oxygen`
   * question on the NEWS2 Chart
   */
  oxygenSaturationScale: VitalParameterScore;
}

export interface QuroVitalParameterScores {
  pulseRate: VitalParameterResult;
  respirationRate: VitalParameterResult;
  temperature: VitalParameterResult;
  consciousness: VitalParameterScore;
  /**
   * A score appended depending on the oxygen scale a patient is on. Represents the `Air or Oxygen`
   * question on the NEWS2 Chart
   */
  oxygenSaturationScale: VitalParameterScore;
  oxygenSaturation?: VitalParameterResult;
  systolicBloodPressure?: VitalParameterResult;
}

export type NEWS2Bounds = Bounds<NEWS2ClinicalRisk>;

export type NEWS2Scale = Scale<NEWS2ClinicalRisk>;

/**
 * Score is a number between 0 and 21
 */
export type NEWS2Score = number;

/**
 * Same as the NEWS2Score but does not depend on SpO2 and BP being defined (default to 0)
 */
export type QuroScore = number;

export interface NEWS2Result {
  parameterScores: NEWS2VitalParameterScores;
  score: NEWS2Score;
  risk: NEWS2ClinicalRisk;
}

export interface QuroScoreResult {
  parameterScores: Partial<NEWS2VitalParameterScores>;
  score: QuroScore;
  risk: NEWS2ClinicalRisk;
}
